import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  useTheme,
  useThemeColorValues,
} from "../../../providers/ThemeProvider";

/**
 * Renders model-authored HTML inside a sandboxed iframe.
 *
 * ## Why an iframe and not a div
 *
 * The briefing HTML is generated by an LLM from a prompt that contains machine
 * names, alarm texts and user-authored chart comments. Any of those can carry
 * markup, so the output must be treated as hostile. Injecting it with
 * `dangerouslySetInnerHTML` — even into a small container on one tab — runs it
 * in this origin, where it could read the Keycloak token, call NATS and Doris as
 * the signed-in user, and exfiltrate. A `<div>` is a layout box, not a security
 * boundary.
 *
 * An iframe with `sandbox` and a `srcdoc` *is* a boundary: the content lands in
 * an opaque origin with no access to the parent DOM, our storage, or cookies.
 *
 * ## Do not add `allow-same-origin`
 *
 * `allow-scripts` is granted so the frame can report its own height (and so the
 * model may use small amounts of interactivity). Adding `allow-same-origin`
 * alongside `allow-scripts` would let the framed document reach back into this
 * origin and would defeat the entire sandbox. Likewise `allow-top-navigation`
 * would let it redirect the app. Neither is granted, which is also why links
 * inside the frame are inert — machine navigation is rendered as real React
 * controls outside it.
 */

/** Fallback height before the frame reports its own. */
const INITIAL_HEIGHT = 420;

/** Ceiling so a runaway response can't produce an endless frame. */
const DEFAULT_MAX_HEIGHT = 4000;

/**
 * Injected into the frame to report its content height to the parent.
 *
 * `postMessage` targets '*' because the framed document sits in an opaque
 * origin and genuinely cannot know the parent's origin. That's safe here: the
 * only thing sent outward is a number, and the parent authenticates the message
 * by object identity of `event.source`, not by origin.
 */
const RESIZE_SCRIPT = `
<script>
(function () {
  var last = -1;
  function report() {
    var h = Math.ceil(document.documentElement.scrollHeight);
    if (h === last) return;
    last = h;
    parent.postMessage({ __cockpitFrame: 'height', height: h }, '*');
  }
  document.addEventListener('DOMContentLoaded', report);
  window.addEventListener('load', report);
  window.addEventListener('resize', report);
  if (window.ResizeObserver) {
    try { new ResizeObserver(report).observe(document.documentElement); } catch (e) {}
  }
  // Web fonts and images settle after load, so re-measure briefly.
  var ticks = 0;
  var timer = setInterval(function () { report(); if (++ticks > 20) clearInterval(timer); }, 150);
  report();
})();
<\/script>`;

interface FrameTheme {
  bg: string;
  surface: string;
  text: string;
  textMuted: string;
  border: string;
  accent: string;
  success: string;
  warning: string;
  error: string;
}

/**
 * Base stylesheet for the frame.
 *
 * The frame inherits none of the app's CSS, so the current theme is passed in as
 * custom properties. This both keeps dark mode working and gives the model a
 * documented palette to reference (the prompt names these variables).
 */
function baseStyles(theme: FrameTheme, isDark: boolean): string {
  return `
<style>
  :root {
    color-scheme: ${isDark ? "dark" : "light"};
    --bg: ${theme.bg};
    --surface: ${theme.surface};
    --text: ${theme.text};
    --text-muted: ${theme.textMuted};
    --border: ${theme.border};
    --accent: ${theme.accent};
    --success: ${theme.success};
    --warning: ${theme.warning};
    --error: ${theme.error};
  }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; background: transparent; }
  body {
    padding: 4px 2px 8px;
    color: var(--text);
    font-family: ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
    font-size: 15px;
    line-height: 1.6;
    overflow-wrap: break-word;
  }
  h1, h2, h3, h4 { color: var(--text); line-height: 1.3; margin: 0 0 10px; }
  h1 { font-size: 24px; letter-spacing: -0.01em; }
  h2 { font-size: 19px; }
  h3 { font-size: 16px; }
  /* Prose shouldn't span a 27" monitor; tiles and charts still go full width. */
  p, ul, ol { margin: 0 0 12px; max-width: 78ch; }
  ul, ol { padding-left: 20px; }
  li { margin-bottom: 4px; }
  li::marker { color: var(--text-muted); }
  strong, b { font-weight: 650; }
  a { color: var(--accent); }
  /* Floor on secondary text. The model reaches for <small> and .muted for
     captions, and the browser default plus a muted colour left them barely
     readable. */
  small, .muted { color: var(--text-muted); font-size: 13px; }
  /* Floor on body text. Note this is a minimum only — the cap on model-styled
     blocks is further down, since inline styles need !important to override. */
  p, li, td, th { font-size: max(1em, 13px); }
  code, pre {
    font-family: "IBM Plex Mono", ui-monospace, monospace;
    background: var(--surface);
    border-radius: 4px;
  }
  code { padding: 1px 4px; }
  pre { padding: 10px; overflow-x: auto; }
  /* Nothing may overflow the frame horizontally. */
  img, svg, video, canvas, table, pre { max-width: 100%; }
  img, svg { height: auto; }
  table { border-collapse: collapse; width: 100%; font-size: 13px; }
  th, td { border: 1px solid var(--border); padding: 6px 8px; text-align: left; }
  th { background: var(--surface); font-weight: 600; }
  .card, .kpi, .tile {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 10px;
    padding: 10px 12px;
  }
  /* The model tends to leave a lone caption or chart in a card, and generous
     padding plus default margins then stacked into a lot of dead vertical space.
     Collapse the outer margins so the card hugs its content. */
  .card > :first-child, .tile > :first-child, .section > :first-child { margin-top: 0; }
  .card > :last-child, .tile > :last-child { margin-bottom: 0; }
  .grid { display: grid; gap: 12px; grid-template-columns: repeat(auto-fit, minmax(190px, 1fr)); }
  .badge {
    display: inline-block; padding: 2px 8px; border-radius: 999px;
    font-size: 12px; font-weight: 600;
  }
  .badge-success { background: color-mix(in srgb, var(--success) 18%, transparent); color: var(--success); }
  .badge-warning { background: color-mix(in srgb, var(--warning) 18%, transparent); color: var(--warning); }
  .badge-error   { background: color-mix(in srgb, var(--error) 18%, transparent);   color: var(--error); }

  /* ── Dashboard kit ──────────────────────────────────────────────────────
     Real visuals without a charting library: none can be loaded here, since
     the sandbox blocks external resources. So the graphics are CSS shapes and
     inline SVG, and the model only has to write semantic markup. */

  .kpi { display: flex; flex-direction: column; gap: 4px; }
  .kpi-label {
    font-size: 12px; text-transform: uppercase; letter-spacing: 0.04em;
    font-weight: 600; color: var(--text-muted);
  }
  .kpi-value { font-size: 26px; font-weight: 700; font-variant-numeric: tabular-nums; }
  .kpi-delta { font-size: 13px; font-weight: 600; font-variant-numeric: tabular-nums; }
  .up { color: var(--success); }
  .down { color: var(--error); }
  .flat { color: var(--text-muted); }

  /* Horizontal bar — set the fill width inline, e.g. style="width:72%" */
  .bar {
    height: 8px; border-radius: 999px; background: var(--border);
    overflow: hidden; margin-top: 4px;
  }
  .bar-fill { height: 100%; border-radius: 999px; background: var(--accent); }
  .bar-fill.good { background: var(--success); }
  .bar-fill.warn { background: var(--warning); }
  .bar-fill.bad  { background: var(--error); }

  /* Ring gauge — set the percentage inline, e.g. style="--pct:87" */
  .gauge {
    --pct: 0;
    width: 84px; height: 84px; border-radius: 50%;
    background: conic-gradient(var(--accent) calc(var(--pct) * 3.6deg), var(--border) 0);
    display: grid; place-items: center; position: relative;
  }
  .gauge.good { background: conic-gradient(var(--success) calc(var(--pct) * 3.6deg), var(--border) 0); }
  .gauge.warn { background: conic-gradient(var(--warning) calc(var(--pct) * 3.6deg), var(--border) 0); }
  .gauge.bad  { background: conic-gradient(var(--error)   calc(var(--pct) * 3.6deg), var(--border) 0); }
  .gauge::after {
    content: ""; position: absolute; inset: 9px;
    border-radius: 50%; background: var(--bg);
  }
  .gauge > * {
    position: relative; z-index: 1;
    font-size: 15px; font-weight: 700; font-variant-numeric: tabular-nums;
  }

  /* Labelled rows for rankings (worst machines, top stop causes, …) */
  .row {
    display: flex; align-items: center; justify-content: space-between;
    gap: 10px; padding: 6px 0; border-bottom: 1px solid var(--border);
  }
  .row:last-child { border-bottom: 0; }
  .row-label { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .row-value { font-variant-numeric: tabular-nums; font-weight: 600; flex: 0 0 auto; }

  /* The model often uses row-label/row-value as siblings directly inside a card
     rather than nested in a .row, where the flex rules above don't apply and they
     rendered as two plain lines. Treat that spelling as a KPI tile, which is
     clearly the intent. */
  .card > .row-label {
    font-size: 12px; text-transform: uppercase; letter-spacing: 0.04em;
    font-weight: 600; color: var(--text-muted); white-space: normal;
  }
  .card > .row-value {
    font-size: 20px; font-weight: 700; margin-top: 2px;
  }

  /* Text lifted out of a text-only svg (see unwrapTextSvg). Centred, because the
     model was using the svg to present a headline fault rather than a table row. */
  .text-callout { text-align: center; padding: 6px 0 2px; }
  .text-callout-main { font-size: 18px; font-weight: 700; line-height: 1.35; }
  .text-callout-sub {
    font-size: 13px; color: var(--text-muted); margin-top: 2px;
  }

  /* Inline SVG charts.
     Width and height are pinned from the viewBox before injection (see
     normaliseSvgScale), so these must NOT force a width — that would rescale the
     drawing and resize its text with it. max-width only shrinks the chart on a
     narrow window, which is ordinary responsive behaviour, and the auto inline
     margin centres one that ends up narrower than its card instead of leaving it
     against the left edge. */
  .spark, .chart {
    display: block;
    max-width: 100%;
    height: auto;
    margin-inline: auto;
  }

  /* Charts the model forgot to class.
     A bare <svg> keeps its intrinsic attribute size, which rendered as a
     postage stamp adrift in whitespace. Any svg placed as a block-level child
     of a container is a chart and should fill the width — but NOT svgs nested
     in a span, badge or row, which are icons. */
  body > svg, .card > svg, .tile > svg, .section > svg, .grid > svg, figure > svg {
    display: block;
    max-width: 100%;
    height: auto;
    margin: 2px auto;
  }
  /* Strokes must not thicken when a chart is scaled up. */
  svg path, svg polyline, svg line, svg rect, svg circle, svg ellipse {
    vector-effect: non-scaling-stroke;
  }
  /* Unclassed charts still get themed strokes rather than a hard-coded colour. */
  .card > svg path, .section > svg path, body > svg path,
  .card > svg polyline, .section > svg polyline, body > svg polyline {
    vector-effect: non-scaling-stroke;
  }
  .spark path, .spark polyline, .chart path, .chart polyline {
    fill: none; stroke: var(--accent); stroke-width: 2;
    vector-effect: non-scaling-stroke;
  }
  .spark .area, .chart .area { fill: var(--accent); opacity: 0.14; stroke: none; }
  .chart .axis { stroke: var(--border); stroke-width: 1; }
  .chart text, .spark text { fill: var(--text-muted); font-size: 10px; stroke: none; }
  .chart rect { fill: var(--accent); }
  .chart rect.good { fill: var(--success); }
  .chart rect.warn { fill: var(--warning); }
  .chart rect.bad  { fill: var(--error); }

  .section { margin-top: 16px; }
  .section:first-child { margin-top: 0; }
  .section-title {
    font-size: 12px; text-transform: uppercase; letter-spacing: 0.06em;
    font-weight: 700; color: var(--text-muted);
    margin-bottom: 10px; padding-bottom: 6px;
    border-bottom: 1px solid var(--border);
  }

  /* Cards the model colours inline for severity still need breathing room. */
  .card + .card, .tile + .tile { margin-top: 10px; }
  .card p:last-child, .tile p:last-child, .card ul:last-child { margin-bottom: 0; }

  /* ── Taming inline styling ───────────────────────────────────────────────
     The model sometimes styles plain blocks itself — 22px centred text with
     heavy padding and a fixed height — which produced a tall, mostly empty card
     with one oversized caption adrift in it.

     These rules only touch elements carrying no class of ours, so anything using
     the kit (kpi-value, section-title, gauge, badge…) keeps its intended size.
     !important is required because the model's styling is inline, which
     otherwise always wins. */
  .card div:not([class]), .card p:not([class]), .card span:not([class]),
  .tile div:not([class]), .tile p:not([class]),
  .section > div:not([class]), .section > p:not([class]) {
    font-size: 15px !important;
    line-height: 1.5 !important;
    text-align: left !important;
    padding: 0 !important;
    min-height: 0 !important;
    height: auto !important;
  }
  /* Same for unclassed headings, which arrive as oversized pseudo-titles. */
  .card h1:not([class]), .card h2:not([class]), .card h3:not([class]) {
    font-size: 16px !important;
    text-align: left !important;
    margin: 0 0 6px !important;
  }
</style>`;
}

/** Whether the model returned a whole document rather than a fragment. */
function isFullDocument(html: string): boolean {
  return /<html[\s>]/i.test(html) || /<!DOCTYPE\s+html/i.test(html);
}

/** Width charts are normalised to, roughly the panel's inner width. */
const CHART_TARGET_WIDTH = 900;

/** Tallest a chart may render, so a square-ish viewBox can't fill the screen. */
const CHART_MAX_HEIGHT = 440;

/** Intended on-screen size of chart labels, in real pixels. */
const CHART_TEXT_PX = 13;

/** Elements that mean an svg is actually drawing something. */
const SVG_GEOMETRY =
  /<(?:path|rect|circle|ellipse|line|polyline|polygon|image|use)\b/i;

/**
 * Convert an svg that only holds text into plain HTML.
 *
 * The model sometimes uses an svg as a text container — a "spark" with two `text`
 * nodes and no geometry — to show a fault name and a duration. Nothing good comes
 * of keeping that as an svg: scaled up it became a 270px-tall box with the words
 * stranded near the bottom, and pinned to its viewBox its `font-size="11"` user
 * units render smaller than the surrounding UI with no way to restyle them.
 *
 * Lifting the text out gives it the panel's own typography: first line as the
 * value, the rest as muted captions.
 */
function unwrapTextSvg(body: string): string {
  const lines: string[] = [];
  const text = /<text\b[^>]*>([\s\S]*?)<\/text>/gi;
  let match: RegExpExecArray | null;
  while ((match = text.exec(body)) !== null) {
    // Drop any nested tspan/markup, keeping the words.
    const line = (match[1] ?? "").replace(/<[^>]+>/g, "").trim();
    if (line) lines.push(line);
  }

  // No text either — an empty or decorative svg, so drop it rather than leaving a
  // sized box behind.
  if (lines.length === 0) return "";

  const [first, ...rest] = lines;
  return [
    // Wrapped in its own class so it can be centred without affecting the
    // label/value pairs elsewhere, which read better left-aligned.
    `<div class="text-callout">`,
    `<div class="text-callout-main">${first}</div>`,
    ...rest.map((line) => `<div class="text-callout-sub">${line}</div>`),
    `</div>`,
  ].join("");
}

function normaliseSvgScale(html: string): string {
  return html.replace(
    /<svg\b([^>]*)>([\s\S]*?)<\/svg>/gi,
    (whole, rawAttrs: string, body: string) => {
      /**
       * Leave text-only svgs at their natural size.
       *
       * The model sometimes uses an svg purely as a text container — a "spark" with
       * two `text` nodes and no geometry. Scaling that to chart width turned two
       * lines of caption into a 270px-tall box with the text pinned near its bottom
       * edge, which is where the huge empty cards came from. If nothing is being
       * drawn, there is nothing to scale.
       */
      if (!SVG_GEOMETRY.test(body)) return unwrapTextSvg(body);

      return scaleChart(whole, rawAttrs, body);
    },
  );
}

/**
 * Scale a real chart to a consistent width, counter-scaling its labels.
 *
 * SVG text is measured in user units, so it scales with the drawing: the same
 * `font-size` renders large in a small viewBox stretched wide, and unreadably
 * small in a big one shrunk down. Dividing the target size by the scale applied
 * here means labels land at CHART_TEXT_PX on screen for any viewBox.
 *
 * Computed at build time rather than measured in the browser — an earlier runtime
 * version read widths before layout settled and got the factor wrong.
 */
function scaleChart(whole: string, rawAttrs: string, body: string): string {
  const viewBox = rawAttrs.match(
    /viewBox\s*=\s*["']\s*[-\d.]+[\s,]+[-\d.]+[\s,]+([\d.]+)[\s,]+([\d.]+)\s*["']/i,
  );

  // Fall back to width/height attributes when there's no viewBox, so an svg sized
  // purely by attributes isn't skipped and left at whatever size the model chose.
  const attrWidth = rawAttrs.match(
    /\bwidth\s*=\s*["']\s*([\d.]+)\s*(?:px)?\s*["']/i,
  );
  const attrHeight = rawAttrs.match(
    /\bheight\s*=\s*["']\s*([\d.]+)\s*(?:px)?\s*["']/i,
  );

  const boxWidth = Number(viewBox ? viewBox[1] : attrWidth?.[1]);
  const boxHeight = Number(viewBox ? viewBox[2] : attrHeight?.[1]);
  if (!Number.isFinite(boxWidth) || !Number.isFinite(boxHeight)) return whole;
  if (boxWidth <= 0 || boxHeight <= 0) return whole;

  // Leave small decorative marks alone — an icon is not a chart.
  if (boxWidth < 120) return whole;

  // Scale to a consistent rendered width, then pull back if that would let an
  // unusually tall viewBox dominate the panel.
  let scale = CHART_TARGET_WIDTH / boxWidth;
  if (boxHeight * scale > CHART_MAX_HEIGHT)
    scale = CHART_MAX_HEIGHT / boxHeight;

  const width = Math.round(boxWidth * scale);
  const height = Math.round(boxHeight * scale);

  // Drop any width/height the model set — including percentages, which would
  // reintroduce arbitrary scaling.
  const attrs = rawAttrs
    .replace(/\s(?:width|height)\s*=\s*["'][^"']*["']/gi, "")
    .trim();
  const addViewBox = viewBox ? "" : ` viewBox="0 0 ${boxWidth} ${boxHeight}"`;

  // !important is needed to beat font-size attributes on the text nodes.
  const textSize = (CHART_TEXT_PX / scale).toFixed(2);
  const style = `<style>text{font-size:${textSize}px !important}</style>`;

  return `<svg ${attrs}${addViewBox} width="${width}" height="${height}">${style}${body}</svg>`;
}

/**
 * Assemble the `srcdoc`.
 *
 * Models sometimes return a bare fragment and sometimes a full document, so our
 * stylesheet and resize script are injected into whichever we got. This is about
 * rendering fidelity, not safety — the sandbox is what contains the content
 * either way.
 */
function buildSrcDoc(
  rawHtml: string,
  theme: FrameTheme,
  isDark: boolean,
): string {
  const html = normaliseSvgScale(rawHtml);
  const head = `${baseStyles(theme, isDark)}${RESIZE_SCRIPT}`;

  if (isFullDocument(html)) {
    if (/<head[\s>]/i.test(html)) {
      return html.replace(/<head([^>]*)>/i, `<head$1>${head}`);
    }
    return html.replace(/<html([^>]*)>/i, `<html$1><head>${head}</head>`);
  }

  return `<!DOCTYPE html><html><head><meta charset="utf-8">${head}</head><body>${html}</body></html>`;
}

export interface SandboxedHtmlFrameProps {
  html: string;
  /** Accessible name for the frame. */
  title: string;
  className?: string;
  maxHeight?: number;
}

const SandboxedHtmlFrame: React.FC<SandboxedHtmlFrameProps> = ({
  html,
  title,
  className = "",
  maxHeight = DEFAULT_MAX_HEIGHT,
}) => {
  const { currentTheme } = useTheme();
  const colors = useThemeColorValues();
  const frameRef = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(INITIAL_HEIGHT);

  const theme: FrameTheme = useMemo(
    () => ({
      bg: colors.bgPrimary,
      surface: colors.bgSecondary,
      text: colors.textPrimary,
      textMuted: colors.textMuted,
      border: colors.borderPrimary,
      accent: colors.info,
      success: colors.success,
      warning: colors.warning,
      error: colors.error,
    }),
    [colors],
  );

  const srcDoc = useMemo(
    () => buildSrcDoc(html, theme, currentTheme.isDark),
    [html, theme, currentTheme.isDark],
  );

  // Re-measure from scratch whenever the document changes, so switching theme
  // or briefing can shrink the frame as well as grow it.
  useEffect(() => {
    setHeight(INITIAL_HEIGHT);
  }, [srcDoc]);

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      // The frame is in an opaque origin ("null"), so origin checks are
      // meaningless here. Identity of the sending window is the real check:
      // it proves the message came from *our* frame and not another tab.
      if (event.source !== frameRef.current?.contentWindow) return;

      const data = event.data as { __cockpitFrame?: string; height?: number };
      if (!data || data.__cockpitFrame !== "height") return;
      if (typeof data.height !== "number" || !Number.isFinite(data.height))
        return;

      setHeight(Math.min(Math.max(Math.ceil(data.height), 80), maxHeight));
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [maxHeight]);

  return (
    <iframe
      ref={frameRef}
      title={title}
      srcDoc={srcDoc}
      // allow-scripts ONLY. See the file header: adding allow-same-origin here
      // would defeat the sandbox entirely.
      sandbox="allow-scripts"
      referrerPolicy="no-referrer"
      className={`w-full border-0 block ${className}`}
      style={{ height }}
    />
  );
};

export default SandboxedHtmlFrame;
